import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTachometerAlt, faUsers, faBuilding, faHandHoldingHeart, faChartLine, faSpinner, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { adminStatsAPI } from '../../services/adminAPI';
import { toast } from 'react-toastify';
import './admin.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    setLoading(true);
    console.log('Fetching dashboard stats...');
    console.log('Admin token:', localStorage.getItem('admin_access_token') ? 'Present' : 'Missing');
    
    try {
      console.log('Making API request to:', '/api/admin/stats/dashboard');
      const response = await adminStatsAPI.getDashboard();
      console.log('Dashboard response:', response);
      console.log('Dashboard data:', response.data);
      setStats(response.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="admin-empty-state">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <h3>Failed to Load Dashboard</h3>
        <p>Unable to retrieve system statistics</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>
          <FontAwesomeIcon icon={faTachometerAlt} />
          Dashboard
        </h1>
        <p>Overview of system statistics and activity</p>
      </div>

      {/* User Stats */}
      <h2 style={{ color: 'var(--admin-text)', marginBottom: '1rem', fontSize: '1.25rem' }}>
        User Statistics
      </h2>
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon primary">
              <FontAwesomeIcon icon={faUsers} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.users.total.toLocaleString()}</div>
          <div className="admin-stat-label">Total Users</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon success">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.users.active.toLocaleString()}</div>
          <div className="admin-stat-label">Active Users</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon warning">
              <FontAwesomeIcon icon={faUsers} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.users.free.toLocaleString()}</div>
          <div className="admin-stat-label">Free Users</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon primary">
              <FontAwesomeIcon icon={faUsers} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.users.premium.toLocaleString()}</div>
          <div className="admin-stat-label">Premium Users</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon success">
              <FontAwesomeIcon icon={faBuilding} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.users.enterprise.toLocaleString()}</div>
          <div className="admin-stat-label">Enterprise Users</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon primary">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.users.new_this_week.toLocaleString()}</div>
          <div className="admin-stat-label">New This Week</div>
        </div>
      </div>

      {/* Company Stats */}
      <h2 style={{ color: 'var(--admin-text)', marginBottom: '1rem', marginTop: '2rem', fontSize: '1.25rem' }}>
        Company Statistics
      </h2>
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon primary">
              <FontAwesomeIcon icon={faBuilding} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.companies.total.toLocaleString()}</div>
          <div className="admin-stat-label">Total Companies</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon success">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.companies.active.toLocaleString()}</div>
          <div className="admin-stat-label">Active Companies</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon warning">
              <FontAwesomeIcon icon={faUsers} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.companies.total_employees.toLocaleString()}</div>
          <div className="admin-stat-label">Total Employees</div>
        </div>
      </div>

      {/* Task Stats */}
      <h2 style={{ color: 'var(--admin-text)', marginBottom: '1rem', marginTop: '2rem', fontSize: '1.25rem' }}>
        Task Statistics
      </h2>
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon primary">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.tasks.total.toLocaleString()}</div>
          <div className="admin-stat-label">Total Tasks</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon warning">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.tasks.active.toLocaleString()}</div>
          <div className="admin-stat-label">Active Tasks</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon success">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.tasks.completed.toLocaleString()}</div>
          <div className="admin-stat-label">Completed Tasks</div>
        </div>
      </div>

      {/* Donation Stats */}
      <h2 style={{ color: 'var(--admin-text)', marginBottom: '1rem', marginTop: '2rem', fontSize: '1.25rem' }}>
        Donation Statistics
      </h2>
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon primary">
              <FontAwesomeIcon icon={faHandHoldingHeart} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.donations.total_causes.toLocaleString()}</div>
          <div className="admin-stat-label">Total Causes</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon success">
              <FontAwesomeIcon icon={faHandHoldingHeart} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.donations.total_donations.toLocaleString()}</div>
          <div className="admin-stat-label">Total Donations</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon success">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
          </div>
          <div className="admin-stat-value">
            ₦{stats.donations.total_amount.toLocaleString()}
          </div>
          <div className="admin-stat-label">Total Amount Raised</div>
        </div>
      </div>

      {/* Health Stats */}
      <h2 style={{ color: 'var(--admin-text)', marginBottom: '1rem', marginTop: '2rem', fontSize: '1.25rem' }}>
        Health & Wellbeing
      </h2>
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon success">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.health.active_recovery_sessions.toLocaleString()}</div>
          <div className="admin-stat-label">Active Recovery Sessions</div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-header">
            <div className="admin-stat-icon danger">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
          </div>
          <div className="admin-stat-value">{stats.health.high_burnout_risk_users.toLocaleString()}</div>
          <div className="admin-stat-label">High Burnout Risk Users</div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
